import React, { useState } from 'react';
import { View, Modal, Pressable, StyleSheet, Linking, Platform, ActivityIndicator } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Theme } from '../../utils/theme';
import Text from './Text';
import Card from './Card';
import Button from './Button';
import { generateInstallTokenForWeb } from '../../services/appsFlyerAuthService';
import { buildOneLinkURL } from '../../config/appsflyer';
import { auth } from '../../config/firebase';

const APP_STORE_URL = 'https://www.mentorist.app/goto_appstore/';
const PLAY_STORE_URL = 'https://www.mentorist.app/goto_googleplay/';

interface InstallAppPromptProps {
  visible: boolean;
  onDismiss: () => void;
}

const openStoreLink = async (platform: 'ios' | 'android'): Promise<void> => {
  try {
    if (Platform.OS === 'web') {
      const tokenResult = await generateInstallTokenForWeb();
      
      if (tokenResult.success && tokenResult.data) {
        const oneLinkURL = buildOneLinkURL(tokenResult.data, platform);
        await Linking.openURL(oneLinkURL);
        return;
      }
    }
    
    const fallbackURL = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
    await Linking.openURL(fallbackURL);
  } catch (error) {
    console.error('[InstallAppPrompt] Failed to open store link:', error);
    try {
      const directURL = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
      await Linking.openURL(directURL);
    } catch (fallbackError) {
      console.error('[InstallAppPrompt] Fallback failed:', fallbackError);
    }
  }
};

const InstallAppPrompt: React.FC<InstallAppPromptProps> = ({ visible, onDismiss }) => {
  const [loading, setLoading] = useState<'ios' | 'android' | null>(null);

  const handleStorePress = async (platform: 'ios' | 'android') => {
    setLoading(platform);
    try {
      await openStoreLink(platform);
    } finally {
      setTimeout(() => setLoading(null), 1000);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <Pressable style={styles.backdrop} onPress={onDismiss}>
        <Pressable style={styles.contentWrapper} onPress={(event) => event.stopPropagation()}>
          <Card style={styles.card}>
            <View style={styles.iconContainer}>
              <Ionicons name="phone-portrait-outline" size={28} color={Theme.colors.primary} />
            </View>

            <Text variant="h2" style={styles.title}>
              Get the Full App
            </Text>
            <Text variant="body1" style={styles.description}>
              Install the mobile app to access audio playback and subscriptions.
              {Platform.OS === 'web' && '\n\nYou\'ll be automatically signed in after installing.'}
            </Text>

            <View style={styles.storeButtons}>
              <Pressable
                style={[styles.storeButton, loading === 'ios' && styles.storeButtonDisabled]}
                onPress={() => handleStorePress('ios')}
                disabled={loading !== null}
              >
                {loading === 'ios' ? (
                  <ActivityIndicator color={Theme.colors.surface} />
                ) : (
                  <>
                    <Text variant="body1" style={styles.storeButtonText}>
                      Download on App Store
                    </Text>
                    <Ionicons name="logo-apple" size={20} color={Theme.colors.surface} />
                  </>
                )}
              </Pressable>
              <Pressable
                style={[styles.storeButton, loading === 'android' && styles.storeButtonDisabled]}
                onPress={() => handleStorePress('android')}
                disabled={loading !== null}
              >
                {loading === 'android' ? (
                  <ActivityIndicator color={Theme.colors.surface} />
                ) : (
                  <>
                    <Text variant="body1" style={styles.storeButtonText}>
                      Get it on Google Play
                    </Text>
                    <Ionicons name="logo-google-playstore" size={20} color={Theme.colors.surface} />
                  </>
                )}
              </Pressable>
            </View>

            <Button title="Not now" variant="secondary" onPress={onDismiss} />
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Theme.spacing.lg,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 420,
  },
  card: {
    padding: Theme.spacing.xl,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Theme.colors.primary + '20',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: Theme.spacing.md,
  },
  title: {
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  description: {
    textAlign: 'center',
    color: Theme.colors.textSecondary,
    marginBottom: Theme.spacing.lg,
  },
  storeButtons: {
    gap: Theme.spacing.sm,
    marginBottom: Theme.spacing.md,
  },
  storeButton: {
    backgroundColor: Theme.colors.success,
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Theme.spacing.sm,
    ...Theme.shadows.sm,
  },
  storeButtonDisabled: {
    opacity: 0.6,
  },
  storeButtonText: {
    color: Theme.colors.surface,
    fontWeight: '700',
    fontSize: 16,
  },
});

export default InstallAppPrompt;
